import { useMemo, useRef, useState } from "react";
import { Paperclip, X, FileText, Monitor, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { useQuery } from "@/lib/useQuery";
import type { Doc } from "@/lib/types";
import { shortDate, titleCase } from "@/lib/format";
import { Button, Modal, Field, Loading, Tag, toast } from "@/components/ui";

/** An attachment waiting to go out: a file read from the PC, or a document already stored in OpsDesk. */
export type OutAtt = { kind: "file"; filename: string; content: string; contentType: string; size: number } | { kind: "doc"; documentId: string; filename: string; size: number };

export function toSendPayload(atts: OutAtt[]) {
  return {
    attachments: atts.flatMap((a) => (a.kind === "file" ? [{ filename: a.filename, content: a.content, contentType: a.contentType }] : [])),
    documentIds: atts.flatMap((a) => (a.kind === "doc" ? [a.documentId] : [])),
  };
}

export function AttachPicker({ value, onChange, accountId, partnerId }: { value: OutAtt[]; onChange: (v: OutAtt[]) => void; accountId?: string | null; partnerId?: string | null }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [menu, setMenu] = useState(false);
  const [docs, setDocs] = useState(false);
  const addFiles = async (files: FileList) => {
    const next = [...value];
    for (const file of Array.from(files)) {
      if (file.size > 8 * 1024 * 1024) { toast(`${file.name} is over 8 MB`, true); continue; }
      const bytes = new Uint8Array(await file.arrayBuffer());
      let bin = ""; for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
      next.push({ kind: "file", filename: file.name, content: btoa(bin), contentType: file.type || "application/octet-stream", size: file.size });
    }
    onChange(next);
  };
  return (
    <>
      <input ref={fileRef} type="file" multiple hidden onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ""; }} />
      <span style={{ position: "relative" }} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setMenu(false); }}>
        <Button size="md" onClick={() => setMenu((m) => !m)} aria-haspopup="menu" aria-expanded={menu}><Paperclip size={13} /> Attach <ChevronDown size={12} /></Button>
        {menu && (
          <div className="menu" role="menu" style={{ position: "absolute", left: 0, bottom: "calc(100% + 4px)", minWidth: 200, zIndex: 20 }}>
            <button role="menuitem" className="menu-item row gap-8" onClick={() => { setMenu(false); fileRef.current?.click(); }}><Monitor size={13} /> From this computer</button>
            <button role="menuitem" className="menu-item row gap-8" onClick={() => { setMenu(false); setDocs(true); }}><FileText size={13} /> From documents</button>
          </div>
        )}
      </span>
      {docs && <DocsPickerModal accountId={accountId} partnerId={partnerId} chosen={new Set(value.flatMap((a) => (a.kind === "doc" ? [a.documentId] : [])))} onClose={() => setDocs(false)} onPick={(picked) => { onChange([...value.filter((a) => a.kind !== "doc"), ...picked]); setDocs(false); }} />}
    </>
  );
}

export function AttachmentPills({ value, onChange }: { value: OutAtt[]; onChange: (v: OutAtt[]) => void }) {
  if (!value.length) return null;
  return (
    <div className="row wrap gap-6" style={{ padding: "0 14px 8px" }}>
      {value.map((a, i) => (
        <span key={i} className="pill" title={a.kind === "doc" ? "From documents" : "From this computer"}>
          {a.kind === "doc" ? <FileText size={11} /> : <Paperclip size={11} />} {a.filename} <span className="muted xs">{(a.size / 1024).toFixed(0)} KB</span>
          <button className="btn-link" aria-label={`Remove ${a.filename}`} onClick={() => onChange(value.filter((_, j) => j !== i))}><X size={12} /></button>
        </span>
      ))}
    </div>
  );
}

function DocsPickerModal({ accountId, partnerId, chosen, onClose, onPick }: { accountId?: string | null; partnerId?: string | null; chosen: Set<string>; onClose: () => void; onPick: (docs: OutAtt[]) => void }) {
  const scopeKey = accountId ? `accountId=${accountId}` : partnerId ? `partnerId=${partnerId}` : "";
  const { data: scoped, loading: l1 } = useQuery<Doc[]>(`docs-pick:${scopeKey}`, () => (scopeKey ? api.get<Doc[]>(`/documents?${scopeKey}`) : Promise.resolve([])), [scopeKey]);
  const { data: library, loading: l2 } = useQuery<Doc[]>("docs-pick:library", () => api.get<Doc[]>("/documents?library=1"));
  const [sel, setSel] = useState<Set<string>>(new Set(chosen));
  const [q, setQ] = useState("");
  const match = (d: Doc) => !!(d.key || d.signedPdfKey) && (!q || d.title.toLowerCase().includes(q.toLowerCase()));
  const groups = useMemo(() => [
    { label: accountId ? "This account's documents" : partnerId ? "This partner's documents" : "", docs: (scoped ?? []).filter(match) },
    { label: "Standard docs library", docs: (library ?? []).filter(match) },
  ].filter((g) => g.label), [scoped, library, q, accountId, partnerId]); // eslint-disable-line react-hooks/exhaustive-deps
  const all = [...(scoped ?? []), ...(library ?? [])];
  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const confirm = () => onPick([...sel].map((id) => all.find((d) => d.id === id)).filter((d): d is Doc => !!d).map((d) => ({ kind: "doc", documentId: d.id, filename: d.title, size: d.size ?? 0 })));
  return (
    <Modal title="Attach from documents" onClose={onClose} footer={<><span className="small muted" style={{ marginRight: "auto" }}>{sel.size} selected</span><Button onClick={onClose}>Cancel</Button><Button variant="primary" onClick={confirm}>Attach</Button></>}>
      <Field label="Find"><input className="input" autoFocus placeholder="Title" value={q} onChange={(e) => setQ(e.target.value)} /></Field>
      {(l1 && !scoped) || (l2 && !library) ? <Loading /> : (
        <div className="col gap-12" style={{ maxHeight: "50vh", overflowY: "auto" }}>
          {groups.map((g) => (
            <div key={g.label} className="col gap-4">
              <div className="label">{g.label}</div>
              {g.docs.length === 0 && <div className="small muted">Nothing here{q ? " matching that" : ""}. Only uploaded files can be attached; drafted text documents cannot.</div>}
              {g.docs.map((d) => (
                <label key={d.id} className="row gap-8" style={{ minHeight: 30, cursor: "pointer" }}>
                  <input type="checkbox" checked={sel.has(d.id)} onChange={() => toggle(d.id)} />
                  <span className="truncate" style={{ fontWeight: 500 }}>{d.title}</span>
                  <Tag color="outline">{titleCase(d.category)}</Tag>
                  <span className="xs muted">v{d.version}{d.signedPdfKey ? " · signed" : ""}{d.size ? ` · ${(d.size / 1024).toFixed(0)} KB` : ""} · {shortDate(d.createdAt)}</span>
                </label>
              ))}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
